import {
  SocketNotificationContent,
  SocketNotificationType,
} from 'affiny-core/services/websockets/types';
import { END, eventChannel, EventChannel } from 'redux-saga';
import { config } from '../../env';
import { IWebsocketCredentials } from '../../modules/auth/types';
import { websocketActions } from './actions';
import { mapSocketMessage } from './mapper';

export type Dispatch = (input: {} | END) => void;

const NO_WEBSOCKET_ERROR = 'Cannot subscribe to unknown websocket';

class WSService {
  private websocket: WebSocket | undefined;
  private eventChannel: EventChannel<{}> | undefined;
  private credentials: IWebsocketCredentials | undefined;
  private shouldReconnect = true;

  private dispatch: (input: {} | END) => void;

  public connect() {
    // If the websocket connection is already in the "open" state
    // then do not connect to another one
    try {
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        this.websocket = new WebSocket(
          `${config.env.meeticApi.websocketUrl}/websocket`
        );
      }
    } catch (e) {
      console.error('error while connecting to websocket', e);
    }
  }

  public subscribe(websocketCredentials: IWebsocketCredentials) {
    this.credentials = websocketCredentials;
    this.shouldReconnect = true;
    this.eventChannel = eventChannel(dispatch => {
      this.dispatch = dispatch;

      if (!this.websocket) throw new Error(NO_WEBSOCKET_ERROR);

      this.websocket.onmessage = this.onMessage;
      this.websocket.onclose = this.onClientClose;

      return () => {
        this.shouldReconnect = false;
        this.websocket && this.websocket.close();
      };
    });

    return this.eventChannel;
  }

  public close() {
    if (!this.eventChannel) return;

    this.eventChannel.close();
  }

  private onMessage = (message: MessageEvent) => {
    if (!message.data) return;

    const data = JSON.parse(message.data);

    if (data.connectionResult && data.connectionResult === 'opened') {
      return this.onOpen();
    }

    return this.dispatchMessage(mapSocketMessage(data));
  };

  private onOpen() {
    if (!this.websocket || !this.credentials) return;

    this.websocket.send(JSON.stringify(this.credentials));
  }

  private onClientClose = () => {
    this.onClose(true);
  };

  private onClose = (noMaxRetry = false) => {
    if (this.shouldReconnect && this.dispatch) {
      this.dispatch(websocketActions.reconnect(noMaxRetry));
    }
  };

  private dispatchMessage = (message?: SocketNotificationContent) => {
    if (message) {
      switch (Object.keys(message)[0]) {
        case SocketNotificationType.COUNTER:
          return this.dispatch(
            websocketActions.counterReceived(
              message[SocketNotificationType.COUNTER]
            )
          );
        case SocketNotificationType.EVENT:
          return this.dispatch(
            websocketActions.eventReceived(
              message[SocketNotificationType.EVENT]
            )
          );
      }
    }

    return;
  };
}

export const websocket = new WSService();
